/**
 * The wear layer's authoring rules — D143.
 *
 * @remarks
 * Three rules, all the D31/D47 dead-content law applied to slots, plus the
 * ordinary reference walk over what an equippable says and does:
 *
 * - An item's `equippable.slot` must name a declared `equipmentSlots` entry —
 *   a typo would otherwise mint a slot silently, and two masks would sit on
 *   one face because the engine never learned they wanted the same place.
 * - A declared slot no item can occupy is dead.
 * - The four narrative fields resolve their conditions like every line; the
 *   two effect hooks resolve their references like every effect.
 *
 * @packageDocumentation
 */

import type { ContentPackDefinition } from '@orpheus/content-schema';

import { type ReferenceBrokenFailure } from './load-failure.js';
import { checkNarrativeLines, declaredIdSetsOf } from './reference-walkers.js';
import { brokenReference } from './semantic-validation.js';

/**
 * Validate every equippable declaration against the pack's slot roster.
 *
 * @param contentPack - The parsed pack
 * @returns Every failure found
 */
export const validateEquipmentAuthoring = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];
	const declaredSlots = new Set(contentPack.equipmentSlots);
	const usedSlots = new Set<string>();
	const declared = declaredIdSetsOf(contentPack);

	contentPack.items.forEach((item, itemIndex) => {
		const { equippable } = item;

		if (equippable === undefined) {
			return;
		}

		const path = `items[${String(itemIndex)}].equippable`;

		usedSlots.add(equippable.slot);

		if (!declaredSlots.has(equippable.slot)) {
			failures.push(
				brokenReference(
					`${path}.slot`,
					equippable.slot,
					'this item wants a slot the pack does not declare in equipmentSlots — a typo here would mint a slot nothing else can share',
				),
			);
		}

		checkNarrativeLines(equippable.equipNarratives, `${path}.equipNarratives`, declared, failures);
		checkNarrativeLines(equippable.unequipNarratives, `${path}.unequipNarratives`, declared, failures);
		checkNarrativeLines(equippable.alreadyEquippedNarratives, `${path}.alreadyEquippedNarratives`, declared, failures);
		checkNarrativeLines(equippable.slotOccupiedNarratives, `${path}.slotOccupiedNarratives`, declared, failures);
	});

	contentPack.equipmentSlots.forEach((slot, slotIndex) => {
		if (!usedSlots.has(slot)) {
			failures.push(
				brokenReference(`equipmentSlots[${String(slotIndex)}]`, slot, 'no item can be equipped into this slot, so the declaration is dead'),
			);
		}
	});

	return failures;
};
